import { useNavigate } from '@tanstack/react-router';
import { Eye, History, RefreshCw, Rocket, Trash2, Undo2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';
import { formatDate } from '~/utils/date';

import { useTaxonomyStudio } from '../../context/taxonomy-studio-context';
import { useDiscardDraft } from '../../hooks/use-draft-mutations';
import { useDraftChecks } from '../../hooks/use-taxonomy-draft';
import { useTaxonomyErrors } from '../../hooks/use-taxonomy-errors';
import { DisabledReason } from '../shared/disabled-reason';

import { ChecksPopover } from './checks-popover';
import { PublishDialog } from './publish-dialog';

/**
 * The status bar on every studio page: which version is on screen, and — at the head —
 * the unpublished-change count, checks badge, Review / Publish / Discard. Viewing an
 * earlier version turns it into a warning bar and the whole studio goes read-only.
 */
export function VersionBar() {
  const { t } = useTranslation('taxonomy-studio');
  const navigate = useNavigate();
  const {
    draft,
    live,
    viewing,
    releaseRef,
    setReleaseRef,
    isPast,
    isLiveWhileDraft,
    canPublish,
    othersChanges,
    acknowledgeOthers,
  } = useTaxonomyStudio();
  const checksQuery = useDraftChecks(draft !== null);
  const checks = checksQuery.data;
  const { notify } = useTaxonomyErrors();
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const discard = useDiscardDraft();

  const picker = <HistoryPicker />;

  if (isPast && viewing) {
    return (
      <Bar tone="warning">
        <History className="text-warning h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 text-sm">
          <b>{t('vbar.earlier', { seq: viewing.seq })}</b>{' '}
          <span className="text-muted-foreground">
            {t('vbar.earlierHint', {
              date: viewing.publishedAt ? formatDate(viewing.publishedAt) : '—',
            })}
          </span>
        </span>
        {viewing.status === 'SUPERSEDED' && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 cursor-pointer"
            onClick={() => {
              void navigate({
                to: '/taxonomy/versions',
                search: { seq: String(viewing.seq), view: 'release' },
              });
            }}
          >
            <Undo2 />
            {t('vbar.restore')}
          </Button>
        )}
        <Button
          size="sm"
          className="h-8 cursor-pointer"
          onClick={() => {
            setReleaseRef(undefined);
          }}
        >
          {t('vbar.backToCurrent')}
        </Button>
        {picker}
      </Bar>
    );
  }

  if (isLiveWhileDraft) {
    return (
      <Bar>
        <Eye className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 text-sm">
          <b>{t('vbar.live', { seq: live?.seq ?? '—' })}</b>{' '}
          <span className="text-muted-foreground">{t('vbar.liveReadOnly')}</span>
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-8 cursor-pointer"
          onClick={() => {
            setReleaseRef(undefined);
          }}
        >
          {t('vbar.backToChanges')}
        </Button>
        {picker}
      </Bar>
    );
  }

  if (draft) {
    const n = draft.changeCount;
    const checkErrors = checks?.errors.length ?? 0;
    const publishBlockedReason = !canPublish
      ? t('disabled.noPublishPermission')
      : n === 0
        ? t('disabled.nothingToPublish')
        : checkErrors > 0
          ? t('disabled.checksFailing', { count: checkErrors })
          : null;
    return (
      <>
        <Bar tone="primary">
          <span className="bg-info h-2 w-2 shrink-0 rounded-full" aria-hidden />
          <span className="min-w-0 flex-1 text-sm">
            <b className="block">
              {n ? t('vbar.unpublished', { count: n }) : t('vbar.noUnpublished')}
            </b>
            <span className="text-muted-foreground block text-xs">
              {t('vbar.agentsRead', { seq: live?.seq ?? '—' })}
            </span>
          </span>
          {othersChanges > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 cursor-pointer"
              title={t('vbar.othersHint')}
              onClick={acknowledgeOthers}
            >
              <Users />
              {t('vbar.others', { count: othersChanges })}
              <RefreshCw />
            </Button>
          )}
          {n > 0 && (
            <ChecksPopover
              checks={checks}
              isError={checksQuery.isError}
              error={checksQuery.error}
              onRetry={() => {
                void checksQuery.refetch();
              }}
            />
          )}
          {picker}
          <Button
            size="sm"
            variant="outline"
            className="h-8 cursor-pointer"
            onClick={() => {
              void navigate({
                to: '/taxonomy/versions',
                search: { seq: 'draft', view: 'release' },
              });
            }}
          >
            {t('vbar.review')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 cursor-pointer"
            onClick={() => {
              setDiscardOpen(true);
            }}
          >
            <Trash2 />
            {t('vbar.discard')}
          </Button>
          <DisabledReason reason={publishBlockedReason}>
            <Button
              size="sm"
              className="h-8 cursor-pointer"
              disabled={publishBlockedReason !== null}
              onClick={() => {
                setPublishOpen(true);
              }}
            >
              <Rocket />
              {t('vbar.publish')}
            </Button>
          </DisabledReason>
        </Bar>
        <PublishDialog
          open={publishOpen}
          onOpenChange={setPublishOpen}
          draft={draft}
          checks={checks}
        />
        <ConfirmDialog
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          title={t('discard.title')}
          description={t('discard.description', { count: n, live: live?.seq ?? '—' })}
          confirmLabel={t('discard.confirm')}
          cancelLabel={t('actions.cancel')}
          isPending={discard.isPending}
          onConfirm={() => {
            discard.mutate(undefined, {
              onSuccess: () => {
                toast.success(t('discard.done'));
                setDiscardOpen(false);
                if (releaseRef !== 'draft') setReleaseRef(undefined);
              },
              onError: (err) => {
                notify(err, {
                  retry: () => {
                    discard.mutate();
                  },
                });
              },
            });
          }}
        />
      </>
    );
  }

  return (
    <Bar>
      <span className="bg-success h-2 w-2 shrink-0 rounded-full" aria-hidden />
      <span className="min-w-0 flex-1 text-sm">
        <b>{t('vbar.live', { seq: live?.seq ?? '—' })}</b>{' '}
        <span className="text-muted-foreground">{t('vbar.liveHint')}</span>
      </span>
      {picker}
    </Bar>
  );
}

function Bar({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'primary' | 'warning';
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2',
        tone === 'warning'
          ? 'bg-warning-50 border-warning/30'
          : tone === 'primary'
            ? 'bg-info-50 border-info/20'
            : 'bg-card border-border',
      )}
    >
      {children}
    </div>
  );
}

/**
 * Version picker (GET taxonomy/releases): Unpublished (R15 · n changes), Live, then
 * earlier versions. Picking one sets `?release=`.
 */
function HistoryPicker() {
  const { t } = useTranslation('taxonomy-studio');
  const { releases, draft, live, releaseRef, setReleaseRef } = useTaxonomyStudio();
  const earlier = releases.filter((r) => r.status === 'SUPERSEDED');
  // Normalise numbered refs that name the draft/live to their option value.
  const value =
    releaseRef === 'draft' || (draft && releaseRef === String(draft.seq))
      ? draft
        ? 'draft'
        : 'live'
      : live && releaseRef === String(live.seq)
        ? 'live'
        : releaseRef;

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        setReleaseRef(v === 'draft' ? undefined : v);
      }}
    >
      <SelectTrigger
        className="bg-background h-8 w-auto min-w-40 cursor-pointer gap-2 text-sm"
        aria-label={t('vbar.history')}
      >
        <History className="h-4 w-4" aria-hidden />
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {draft && (
          <SelectItem value="draft">
            {t('vbar.optionDraft', { seq: draft.seq, count: draft.changeCount })}
          </SelectItem>
        )}
        {live && <SelectItem value="live">{t('vbar.optionLive', { seq: live.seq })}</SelectItem>}
        {earlier.map((r) => (
          <SelectItem key={r.seq} value={String(r.seq)}>
            {t('vbar.optionEarlier', { seq: r.seq, name: r.name })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
